import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QuestionIcon } from "../icons/question-icon";
import { CloseIcon } from "../icons/close-icon";
import {
  APISomethingWrongMsg,
  BlockMsg,
  NetworkDisconnectMsg,
} from "src/lib/constants";

interface AlertProps {
  isOpened: boolean;
  setIsOpened: (value: boolean) => void;
  text: string;
  title?: string;
  duration?: number;
}

const Alert: FC<AlertProps> = ({
  isOpened,
  setIsOpened,
  text,
  title,
  duration = 5000,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const safeText = typeof text === "string" ? text : text ? String(text) : "";

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isOpened) {
      timeoutId = setTimeout(() => {
        setIsOpened(false);
      }, duration);
    }
    return () => clearTimeout(timeoutId);
  }, [isOpened, text, duration, setIsOpened]);

  const isError =
    safeText === NetworkDisconnectMsg ||
    safeText === APISomethingWrongMsg ||
    safeText.startsWith("error_") ||
    safeText.toLowerCase().startsWith("error");

  const isErrorTheme = isError || safeText === BlockMsg;

  const makeTitle = () => {
    if (title) return title;
    return isError ? "Error" : "Information";
  };

  const makeText = () => {
    let msg = safeText;
    if (msg.startsWith("error_")) {
      msg = msg.slice(6);
    } else if (msg.toLowerCase().startsWith("error:")) {
      msg = msg.slice(6).trim();
    }
    // Format snake_case error messages (e.g., invalid_iban_format -> Invalid IBAN format)
    if (msg.includes("_") && !msg.includes(" ")) {
      msg = msg.replace(/_/g, " ");
      msg = msg.charAt(0).toUpperCase() + msg.slice(1);
      msg = msg.replace(/\biban\b/gi, "IBAN");
    }
    return msg;
  };

  if (!mounted) return null;

  return createPortal(
    <div
      ref={containerRef}
      style={{ zIndex: 9999999 }}
      className={`fixed top-2 right-2 sm:top-4 sm:right-4 rounded-[22px] flex flex-row items-center justify-center shadow-2xl border border-black/5 dark:border-white/10 ${
        isErrorTheme ? "bg-error" : "bg-active"
      } z-[9999999] w-[calc(100vw-40px)] max-w-[340px] scale-100 text-dark dark:text-light font-manrope transition-all duration-300 ${
        isOpened
          ? "translate-x-0 opacity-100 pointer-events-auto visible"
          : "translate-x-[400px] opacity-0 pointer-events-none invisible"
      }`}
    >
      <div className="flex items-center justify-center w-[87px] rounded-l-[22px]">
        <QuestionIcon
          className={`w-[34px] h-[34px] ${
            isErrorTheme ? "text-white" : "text-primary"
          }`}
        />
      </div>
      <div className="relative flex flex-col items-center justify-center gap-2 w-full px-4 py-4 bg-white h-full rounded-r-[22px]">
        <p className="font-manrope text-[20px] font-medium text-primary w-full text-left">
          {makeTitle()}
        </p>
        <p className="font-manrope text-[14px] break-words font-medium text-[#777E91] w-full text-left">
          {makeText()}
        </p>
        <button
          className="absolute right-0 top-[12px] w-[32px] h-[32px]"
          onClick={() => setIsOpened(false)}
        >
          <CloseIcon className="w-[14px] h-[14px]" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default Alert;

